import {
  DateRangeFilterValueLike,
  dateRangeFilterValueToRange,
} from '@app/components/date-range-selector/date-range/date-range-filter-value-to-range';
import { parseAsDateRangeFilter } from '@app/components/date-range-selector/date-range/parse-as-date-range-filter';
import { GenericDateRangeFilter } from '@app/core/list-view/base-filters/GenericDateRangeFilter';
import { GenericSelectFilter } from '@app/core/list-view/base-filters/GenericSelectFilter';
import { GenericTextFilter } from '@app/core/list-view/base-filters/GenericTextFilter';
import { createFilter } from '@app/core/list-view/types';
import { User, UserRole } from '@mi-front/core';
import { parseAsString } from 'nuqs';
import { defineMessage } from 'react-intl';
import { userRoleLabelMessages } from '../../messages';

/**
 * Local toolbar filters for the admin users list (name, email, role, last login).
 */
export const ADMIN_USERS_FILTERS_CONFIG = [
  createFilter({
    name: 'name',
    label: defineMessage({
      id: 'admin.users.name',
      defaultMessage: 'Nombre',
    }),
    parser: parseAsString.withDefault(''),
    isPermanent: true,
    component: GenericTextFilter,
    componentProps: {
      placeholder: defineMessage({
        id: 'admin.users.filters.namePlaceholder',
        defaultMessage: 'Buscar por nombre',
      }),
      sx: {
        width: '18ch',
      },
    },
    filterFn: (item, value) => {
      const user = item as User;
      const q = typeof value === 'string' ? value.trim().toLowerCase() : '';
      if (!q) {
        return true;
      }
      const fullName = [user.name, user.surname].filter(Boolean).join(' ').toLowerCase();
      return fullName.includes(q);
    },
  }),
  createFilter({
    name: 'email',
    label: defineMessage({
      id: 'common.email',
      defaultMessage: 'Email',
    }),
    parser: parseAsString.withDefault(''),
    isPermanent: true,
    component: GenericTextFilter,
    componentProps: {
      placeholder: defineMessage({
        id: 'admin.users.filters.emailPlaceholder',
        defaultMessage: 'Buscar por email',
      }),
      sx: {
        width: '20ch',
      },
    },
    filterFn: (item, value) => {
      const user = item as User;
      const q = typeof value === 'string' ? value.trim().toLowerCase() : '';
      if (!q) {
        return true;
      }
      return (user.email ?? '').toLowerCase().includes(q);
    },
  }),
  createFilter({
    name: 'role',
    label: defineMessage({
      id: 'admin.users.role',
      defaultMessage: 'Rol',
    }),
    parser: parseAsString,
    isPermanent: true,
    component: GenericSelectFilter,
    componentProps: {
      options: [
        {
          value: UserRole.CLIENT_USER,
          message: userRoleLabelMessages[UserRole.CLIENT_USER],
        },
        {
          value: UserRole.CLIENT_ADMIN,
          message: userRoleLabelMessages[UserRole.CLIENT_ADMIN],
        },
      ],
      sx: {
        minWidth: '14ch',
      },
    },
    filterFn: (item, value) => {
      const user = item as User;
      if (!value) {
        return true;
      }
      return user.role === value;
    },
  }),
  createFilter({
    name: 'lastLogin',
    label: defineMessage({
      id: 'admin.users.lastLogin',
      defaultMessage: 'Último login',
    }),
    parser: parseAsDateRangeFilter,
    isPermanent: true,
    component: GenericDateRangeFilter,
    filterFn: (item, value) => {
      const user = item as User;
      if (!user.lastLogin) {
        return false;
      }
      const { from, to } = dateRangeFilterValueToRange(value as DateRangeFilterValueLike | null);
      const t = new Date(user.lastLogin).getTime();
      if (from != null && t < new Date(from).getTime()) {
        return false;
      }
      if (to != null && t > new Date(to).getTime()) {
        return false;
      }
      return true;
    },
  }),
];
